import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Download, FileBarChart, Truck, AlertTriangle, MessageSquareText, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

function downloadCSV(filename, rows) {
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Reports() {
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => { api.get("/analytics").then((r) => setAnalytics(r.data)).catch(() => {}); }, []);

  const exportPOs = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/pos-export`, "_blank");
  };

  const exportSuppliers = () => {
    if (!analytics) return;
    downloadCSV("supplier_scorecard.csv", [
      ["Supplier", "City", "Total POs", "Active", "Completed", "Delayed", "On-Time %", "Avg Response (h)", "Avg Delay (d)"],
      ...analytics.supplier_table.map((s) => [s.name, s.city, s.total_pos, s.active_pos, s.completed_pos, s.delayed_pos, s.on_time_pct, s.avg_response_hours, s.avg_delay_days]),
    ]);
    toast.success("Supplier scorecard exported");
  };

  const cards = [
    {
      icon: FileBarChart, title: "Purchase Order Register", testId: "report-po",
      desc: "Complete PO export — status, delivery dates, transport mode, tracking, delay days and buyer ownership for every order.",
      action: exportPOs, actionLabel: "Export POs (CSV)",
    },
    {
      icon: Users, title: "Supplier Scorecard", testId: "report-supplier",
      desc: "On-time %, delayed POs, average response time and average delay per supplier.",
      action: exportSuppliers, actionLabel: "Export Scorecard (CSV)",
    },
    {
      icon: Truck, title: "Delivery Performance", testId: "report-delivery",
      desc: analytics ? `On-time delivery is ${analytics.on_time_pct ?? "—"}% with an average delay of ${analytics.avg_delay_days} days across ${analytics.supplier_table.reduce((a, s) => a + s.completed_pos, 0)} completed POs.` : "…",
      action: exportSuppliers, actionLabel: "Export Data (CSV)",
    },
    {
      icon: AlertTriangle, title: "Exception Report", testId: "report-exceptions",
      desc: analytics ? `${analytics.overdue} POs overdue, ${analytics.delayed_pct}% of POs currently delayed, ${analytics.due_this_week} deliveries due this week.` : "…",
      action: exportPOs, actionLabel: "Export POs (CSV)",
    },
    {
      icon: MessageSquareText, title: "Follow-Up Activity", testId: "report-followup",
      desc: analytics ? `${analytics.followups_sent} manual follow-ups and ${analytics.automated_followups} automated follow-ups logged — ~${analytics.effort_saved_hours}h of manual effort saved by automation.` : "…",
      action: exportPOs, actionLabel: "Export POs (CSV)",
    },
  ];

  return (
    <div data-testid="reports-page" className="space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Reports</h1>
        <p className="text-sm text-muted-foreground">Export procurement reports for reviews, audits and supplier meetings.</p>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {cards.map((c, i) => (
          <div key={c.title} data-testid={c.testId} className="flex flex-col rounded-lg border bg-card p-5 animate-fade-up transition-all duration-150 hover:-translate-y-[1px] hover:shadow-sm" style={{ animationDelay: `${i * 50}ms` }}>
            <c.icon className="h-5 w-5 text-slate-700" />
            <h2 className="mt-3 font-heading text-base font-semibold">{c.title}</h2>
            <p className="mt-1.5 flex-1 text-sm text-muted-foreground">{c.desc}</p>
            <Button variant="outline" size="sm" data-testid={`${c.testId}-export`} onClick={c.action} className="mt-4 self-start">
              <Download className="mr-1.5 h-3.5 w-3.5" /> {c.actionLabel}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
